/**
 * Der Fortschritt beim Erzeugen von Probestück und Lied. (11.10.2026, Ticket 3074)
 *
 * Martin, 07.10. 22:49, bei „Risen from the Ashes“: *„Es gab keine Fortschrittsanzeige, nur am Ende das Ergebnis.“* Ein Lied
 * entsteht in 30 bis 90 Sekunden, und bis dahin stand im Chat nichts — ein toter Moment.
 *
 * ⚠️ KEIN Prozentbalken. Der Musikdienst meldet keinen Fortschritt, jede Prozentzahl wäre erfunden. Gezeigt werden nur STUFEN,
 *    die der Server wirklich durchläuft, und die echte Zeit seit dem Start (Regel „Anzeige behauptet nicht zuviel“):
 *      1. text         — Bestellung gegen den bestätigten Vorschlag geprüft (Fingerabdruck von Stil, Länge, Text, Sprache,
 *                        Gesang); erst danach läuft überhaupt etwas an. Die Stufe ist also beim ersten Ereignis schon erledigt.
 *      2. komponieren  — der Musikdienst arbeitet (`anbieter` = der Dienst, der gerade gefragt wird, auch bei einem Ersatz).
 *      3. pruefung     — das Stück liegt vor; Länge, Gesang, Sprache und Text werden geprüft (server/musik-pruefung.ts).
 *    Danach kommt die Karte mit dem Ergebnis, und die Anzeige verschwindet.
 *
 * EIN Besitzer für die Namen und die Form des Ereignisses: der Server (tools/generate_music.ts) schickt es als Feld
 * `fortschritt` am `tool_progress`-Ereignis, das Frontend (frontend/lib/lied-fortschritt.ts) liest es mit `fortschrittAus`.
 */

export const FORTSCHRITT_SCHRITTE = ["text", "komponieren", "pruefung"] as const;
export type FortschrittSchritt = (typeof FORTSCHRITT_SCHRITTE)[number];

/** Die Dienste, die die Kette heute ruft (Suno ist nicht angebunden, Ticket 3003). */
const FORTSCHRITT_ANBIETER = ["lyria", "elevenlabs"] as const;
export type FortschrittAnbieter = (typeof FORTSCHRITT_ANBIETER)[number];

export interface LiedFortschritt {
  schritt: FortschrittSchritt;
  /** Probestück (30 s, zählt nicht) oder das ganze Lied. */
  stufe: "probe" | "lied";
  /** Der Dienst, der gerade komponiert — nur bei `komponieren`, und nur, wenn schon einer gefragt wird. */
  anbieter?: FortschrittAnbieter;
  titel?: string;
}

/** Name der Stufe, wie sie in der Anzeige steht: offen / läuft (Grundform) und erledigt. */
export const SCHRITT_NAME: Readonly<Record<FortschrittSchritt, { grund: string; fertig: string }>> = {
  text: { grund: "Text prüfen", fertig: "Text geprüft" },
  komponieren: { grund: "Komponieren", fertig: "Komponiert" },
  pruefung: { grund: "Prüfung", fertig: "Geprüft" },
};

/** So heißt der Dienst auf der Karte und in der Anzeige. */
export const ANBIETER_ANZEIGE: Readonly<Record<FortschrittAnbieter, string>> = {
  lyria: "Lyria",
  elevenlabs: "ElevenLabs",
};

export function istFortschrittAnbieter(v: unknown): v is FortschrittAnbieter {
  return typeof v === "string" && (FORTSCHRITT_ANBIETER as readonly string[]).includes(v);
}

/** Die Zahl der Stufe (0 = text …); -1, wenn unbekannt. */
export function schrittNr(s: unknown): number {
  return typeof s === "string" ? (FORTSCHRITT_SCHRITTE as readonly string[]).indexOf(s) : -1;
}

/**
 * Das Feld `fortschritt` eines Ereignisses lesen. Von außen kann alles kommen — nur eine bekannte Stufe und Stufenart zählt,
 * der Titel wird gekürzt. Wirft nie; `null` = kein (lesbarer) Fortschritt.
 */
export function fortschrittAus(roh: unknown): LiedFortschritt | null {
  if (!roh || typeof roh !== "object") return null;
  const r = roh as Record<string, unknown>;
  if (schrittNr(r.schritt) < 0) return null;
  const stufe = r.stufe === "probe" ? "probe" : r.stufe === "lied" ? "lied" : null;
  if (!stufe) return null;
  const titel = typeof r.titel === "string" ? r.titel.replace(/\s+/g, " ").trim().slice(0, 120) : "";
  return {
    schritt: r.schritt as FortschrittSchritt,
    stufe,
    ...(istFortschrittAnbieter(r.anbieter) ? { anbieter: r.anbieter } : {}),
    ...(titel ? { titel } : {}),
  };
}
